import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getSupabaseClient } from '../../supabaseClient';
import { useBusinessType } from '../../context/BusinessContext';
import {
  ArrowLeft,
  SpeakerHigh,
  Play,
  Pause,
  Chat,
  CircleNotch,
  WarningCircle,
  Clock,
  CaretDown,
  Check,
  ArrowRight,
  Question,
  Copy
} from '@phosphor-icons/react';
import { Phone } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import useHelpPanel from '../../hooks/use-help-panel';
import HelpPanel from '../../components/HelpPanel';
import TaskSection from '../chats/TaskSection';

// Import the help panel content
import { helpPanelSections } from '../../pages/CallsListPage';

// Refined animation variants with cinematic qualities
const pageVariants = {
  initial: { opacity: 0 },
  animate: { 
    opacity: 1,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1],
      staggerChildren: 0.15
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

const cardVariants = {
  initial: { opacity: 0, y: 40 },
  animate: { 
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.9,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

const sectionVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

const expandVariants = {
  initial: { height: 0, opacity: 0 },
  animate: { 
    height: 'auto', 
    opacity: 1,
    transition: {
      height: {
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1]
      },
      opacity: {
        duration: 0.6,
        delay: 0.15
      }
    }
  }
};

const expandedContentVariants = {
  initial: { height: 0, opacity: 0 },
  animate: { 
    height: 'auto', 
    opacity: 1,
    transition: {
      height: {
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1]
      },
      opacity: {
        duration: 0.4,
        delay: 0.2
      }
    }
  },
  exit: { 
    height: 0,
    opacity: 0,
    transition: {
      height: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      },
      opacity: {
        duration: 0.3
      }
    }
  }
};

const actionSectionVariants = {
  initial: { 
    opacity: 0, 
    y: 20,
    scale: 0.98,
    height: 'auto'
  },
  animate: { 
    opacity: 1,
    y: 0,
    scale: 1,
    height: 'auto',
    transition: {
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1],
      scale: {
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  },
  exit: { 
    opacity: 0,
    height: 0,
    scale: 0.98,
    transition: {
      opacity: { duration: 0.4, ease: [0.22, 1, 0.36, 1] },
      height: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: 0.15 },
      scale: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
    }
  }
};

const completedLinkVariants = {
  initial: { opacity: 0, y: 10 },
  animate: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1],
      delay: 0.4
    }
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: {
      duration: 0.3,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

// More refined status configuration
const statusConfig = {
  issue_resolved: {
    color: 'text-emerald-600',
    background: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    label: 'Resolved',
    icon: () => (
      <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.4"/>
        <path d="M5 8L7 10L11 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )
  },
  action_required: {
    color: 'text-amber-600',
    background: 'bg-amber-50',
    borderColor: 'border-amber-200',
    label: 'Action Required',
    icon: () => (
      <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.4"/>
        <path d="M8 4.5v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="8" cy="12" r="1" fill="currentColor"/>
      </svg>
    )
  },
  incomplete_call: {
    color: 'text-slate-600',
    background: 'bg-slate-50',
    borderColor: 'border-slate-200',
    label: 'Incomplete',
    icon: () => (
      <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.4"/>
        <path d="M5 5L11 11M11 5L5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    )
  }
};

// Refined checkbox design
const AnimatedCheckbox = ({ checked, onChange, id }) => (
  <div className="relative">
    <motion.button
      className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors 
                ${checked ? 'bg-black' : 'bg-transparent border border-slate-300'}`}
      onClick={onChange}
      initial={false}
      animate={{ 
        scale: checked ? 1 : 1,
        transition: { duration: 0.2, ease: [0.22, 1, 0.36, 1] }
      }}
      whileHover={{ 
        scale: 1.1,
        transition: { duration: 0.2, ease: [0.22, 1, 0.36, 1] }
      }}
      whileTap={{ 
        scale: 0.9,
        transition: { duration: 0.1, ease: [0.22, 1, 0.36, 1] }
      }}
    >
      {checked && (
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ 
            scale: 1, 
            opacity: 1,
            transition: { 
              type: "spring", 
              stiffness: 500, 
              damping: 30,
              delay: 0.1
            }
          }}
          exit={{ scale: 0, opacity: 0 }}
        >
          <Check className="w-3 h-3 text-white" />
        </motion.div>
      )}
    </motion.button>
  </div>
);

const ActionItem = ({ item, onComplete }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const formatDueDate = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (item.task_schema.type === 'complex') {
    return (
      <motion.div 
        className="space-y-3"
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
      >
        {/* Complex Task Header */}
        <div className="flex items-start gap-3">
          <AnimatedCheckbox
            checked={item.is_completed}
            onChange={(e) => {
              e.stopPropagation();
              onComplete(item.id, item.is_completed, false);
            }}
            id={item.id}
          />

          <div 
            className="flex-1 cursor-pointer group"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <div className="flex items-center justify-between">
              <motion.span 
                className={`text-sm ${item.is_completed ? 'text-slate-400 line-through' : 'text-slate-800'} font-medium transition-colors`}
                style={{ color: isHovered && !item.is_completed ? '#000' : '' }}
              >
                {item.task_schema.action}
              </motion.span>
              <motion.div
                initial={false}
                animate={{ 
                  rotate: isExpanded ? 180 : 0
                }}
                style={{ 
                  color: isHovered ? '#000' : '#94a3b8' 
                }}
                transition={{ 
                  duration: 0.5, 
                  ease: [0.22, 1, 0.36, 1]
                }}
                className="text-slate-400"
              >
                <CaretDown className="h-4 w-4" />
              </motion.div>
            </div>
            {item.task_schema.timeline && (
              <div className="text-xs text-slate-500 mt-1">
                Due on {formatDueDate(item.task_schema.timeline.due_at)}
              </div>
            )}
          </div>
        </div>

        {/* Subtasks */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div 
              className="ml-8 space-y-3 border-l border-slate-200 pl-4"
              variants={expandedContentVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              {item.task_schema.subtasks.map((subtask) => (
                <motion.div 
                  key={subtask.id} 
                  className="flex items-start gap-3 cursor-pointer group"
                  onClick={() => onComplete(subtask.id, subtask.is_completed, true)}
                >
                  <AnimatedCheckbox
                    checked={subtask.is_completed}
                    onChange={(e) => {
                      e.stopPropagation();
                      onComplete(subtask.id, subtask.is_completed, true);
                    }}
                    id={subtask.id}
                  />
                  <div className="flex-1">
                    <span className={`text-sm ${subtask.is_completed ? 'text-slate-400 line-through' : 'text-slate-600'} group-hover:text-slate-900 transition-colors`}>
                      {subtask.action}
                    </span>
                    {subtask.timeline?.duration_estimate && (
                      <div className="text-xs text-slate-500 mt-1">
                        {subtask.timeline.duration_estimate}
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  }

  // Simple Task
  return (
    <motion.div 
      className="flex items-start gap-3 cursor-pointer group"
      onClick={() => onComplete(item.id, item.is_completed)}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <AnimatedCheckbox
        checked={item.is_completed}
        onChange={(e) => {
          e.stopPropagation();
          onComplete(item.id, item.is_completed, false);
        }}
        id={item.id}
      />
      <div className="flex-1">
        <motion.span 
          className={`inline-block text-sm ${item.is_completed ? 'text-slate-400 line-through' : 'text-slate-800'} transition-colors`}
          style={{ color: isHovered && !item.is_completed ? '#000' : '' }}
        >
          {item.task_schema.action}
        </motion.span>
        {item.task_schema.timeline?.duration_estimate && (
          <div className="text-xs text-slate-500 mt-1.5">
            {item.task_schema.timeline.duration_estimate}
          </div>
        )}
      </div>
    </motion.div>
  );
};

const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';

  // Remove any non-digit characters first
  const cleaned = phoneNumber.replace(/\D/g, '');

  // Handle US/Canada numbers (with or without +1)
  if (cleaned.length === 10 || (cleaned.length === 11 && cleaned.startsWith('1'))) {
    const number = cleaned.length === 11 ? cleaned.slice(1) : cleaned;
    return `(${number.slice(0,3)}) ${number.slice(3,6)}-${number.slice(6)}`;
  }

  // Handle international numbers
  if (cleaned.length > 10) {
    // Extract country code (assume first 2-3 digits after any +1)
    const countryCode = cleaned.startsWith('1') ? cleaned.slice(1,3) : cleaned.slice(0,2);
    const remaining = cleaned.startsWith('1') ? cleaned.slice(3) : cleaned.slice(2);

    // Format remaining numbers
    return `+${countryCode} (${remaining.slice(0,3)}) ${remaining.slice(3,6)}-${remaining.slice(6)}`;
  }

  return phoneNumber; // Return original if we can't format it
};

const formatDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

// Compact date formatter for mobile displays
const formatDateCompact = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

const CallDetailsPage = ({ id: propId, hideBackButton = false }) => {
  const { id: paramId } = useParams();
  const id = propId || paramId; // Use prop if provided, otherwise use URL param
  const navigate = useNavigate();
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);

  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  const [audioState, setAudioState] = useState({
    playing: false,
    currentTime: 0,
    duration: 0,
    volume: 1,
  });
  const audioRef = React.useRef(null);
  const mobileHeaderRef = React.useRef(null);
  const [interfaceTheme, setInterfaceTheme] = useState('light');

  // Help panel hook
  const helpPanel = useHelpPanel({
    title: "Understanding Call Management",
    sections: helpPanelSections,
    position: "right",
    size: "large"
  });

  useEffect(() => {
    const updateRightCardHeight = () => {
      const leftCard = document.getElementById('left-card');
      if (leftCard) {
        document.documentElement.style.setProperty(
          '--left-card-height',
          `${leftCard.offsetHeight}px`
        );
      }
    };

    // Initial update
    updateRightCardHeight();

    // Update on left card content changes
    const resizeObserver = new ResizeObserver(updateRightCardHeight);
    const leftCard = document.getElementById('left-card');
    if (leftCard) {
      resizeObserver.observe(leftCard);
    }

    return () => {
      if (leftCard) {
        resizeObserver.unobserve(leftCard);
      }
    };
  }, [call?.todo_items]); // Re-run when todo items change

  const fetchCallDetails = useCallback(async () => {
    try {
      if (!id) return;
      setLoading(true);
      setError(null);

      const { getUserAccountId } = await import('../../utils/accountUtils');
      const { accountId } = await getUserAccountId();

      const { data, error } = await supabase
        .from('calls')
        .select(`
          *,
          contact:contacts(*),
          call_type:call_types(*),
          todo_items(*)
        `)
        .eq('id', id)
        .eq('account_id', accountId)
        .eq('visible', true)
        .maybeSingle();

      if (error) {
        console.error("Error fetching call details:", error);
        throw new Error('Failed to fetch call details.');
      }

      if (!data) {
        setCall(null);
        throw new Error('Call not found or you do not have permission to view it.');
      }

      setCall(data);

      // Auto-mark as viewed when opening call details (Steve Jobs solution)
      if (!data.viewed) {
        const { error: updateError } = await supabase
          .from('calls')
          .update({ viewed: true })
          .eq('id', id);

        if (updateError) {
          console.error('Error marking call as viewed:', updateError);
        } else {
          // Update local state to reflect the change
          data.viewed = true;
        }
      }

      // Set initial duration from corrected_duration if available
      if (data.corrected_duration) {
        setAudioState(prev => ({
          ...prev,
          duration: Math.round(parseFloat(data.corrected_duration))
        }));
      }

    } catch (err) {
      console.error('Error fetching call details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, supabase]);

  useEffect(() => {
    fetchCallDetails();
  }, [fetchCallDetails]);

  // Dynamic header height calculation
  useEffect(() => {
    const updateMobileHeaderHeight = () => {
      if (mobileHeaderRef.current && window.innerWidth < 768) {
        const headerHeight = mobileHeaderRef.current.offsetHeight;
        document.documentElement.style.setProperty('--mobile-header-height', `${headerHeight}px`);
      }
    };

    // Initial calculation
    updateMobileHeaderHeight();

    // Update on window resize
    const handleResize = () => {
      updateMobileHeaderHeight();
    };

    window.addEventListener('resize', handleResize);

    // Also update when content changes
    const resizeObserver = new ResizeObserver(updateMobileHeaderHeight);
    if (mobileHeaderRef.current) {
      resizeObserver.observe(mobileHeaderRef.current);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (mobileHeaderRef.current) {
        resizeObserver.unobserve(mobileHeaderRef.current);
      }
    };
  }, [call]); // Re-run when call data changes

  // Dynamic header height calculation
  useEffect(() => {
    const updateMobileHeaderHeight = () => {
      if (mobileHeaderRef.current && window.innerWidth < 768) {
        const headerHeight = mobileHeaderRef.current.offsetHeight;
        document.documentElement.style.setProperty('--mobile-header-height', `${headerHeight}px`);
      }
    };

    // Initial calculation
    updateMobileHeaderHeight();

    // Update on window resize
    const handleResize = () => {
      updateMobileHeaderHeight();
    };

    window.addEventListener('resize', handleResize);

    // Also update when content changes
    const resizeObserver = new ResizeObserver(updateMobileHeaderHeight);
    if (mobileHeaderRef.current) {
      resizeObserver.observe(mobileHeaderRef.current);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (mobileHeaderRef.current) {
        resizeObserver.unobserve(mobileHeaderRef.current);
      }
    };
  }, [call]); // Re-run when call data changes

  const handleTaskCompletion = async (taskId, currentState, isSubtask = false) => {
    try {
      // Create a deep copy of the current call state
      const updatedCall = {
        ...call,
        todo_items: [...call.todo_items]
      };

      // If it's a main task
      if (!isSubtask) {
        const mainTaskIndex = updatedCall.todo_items.findIndex(item => item.id === taskId);
        if (mainTaskIndex === -1) return;

        const mainTask = updatedCall.todo_items[mainTaskIndex];

        if (mainTask?.task_schema?.type === 'complex') {
          // For complex tasks, toggle all subtasks to match the main task's new state
          const updatedTaskSchema = {
            ...mainTask.task_schema,
            subtasks: mainTask.task_schema.subtasks.map(subtask => ({
              ...subtask,
              is_completed: !currentState
            }))
          };

          // Update local state first
          updatedCall.todo_items[mainTaskIndex] = {
            ...mainTask,
            is_completed: !currentState,
            completed_at: !currentState ? new Date().toISOString() : null,
            task_schema: updatedTaskSchema
          };

          // Update database
          const { error: mainTaskError } = await supabase
            .from('todo_items')
            .update({
              is_completed: !currentState,
              completed_at: !currentState ? new Date().toISOString() : null,
              task_schema: updatedTaskSchema
            })
            .eq('id', taskId);

          if (mainTaskError) throw mainTaskError;
        } else {
          // For simple tasks, just toggle the completion state
          updatedCall.todo_items[mainTaskIndex] = {
            ...mainTask,
            is_completed: !currentState,
            completed_at: !currentState ? new Date().toISOString() : null
          };

          const { error: simpleTaskError } = await supabase
            .from('todo_items')
            .update({
              is_completed: !currentState,
              completed_at: !currentState ? new Date().toISOString() : null
            })
            .eq('id', taskId);

          if (simpleTaskError) throw simpleTaskError;
        }
      } else {
        // Handle subtask completion
        const parentTaskIndex = updatedCall.todo_items.findIndex(item => 
          item.task_schema.subtasks?.some(subtask => subtask.id === taskId)
        );

        if (parentTaskIndex === -1) return;

        const parentTask = updatedCall.todo_items[parentTaskIndex];

        if (parentTask) {
          const updatedTaskSchema = {
            ...parentTask.task_schema,
            subtasks: parentTask.task_schema.subtasks.map(subtask => 
              subtask.id === taskId 
                ? { 
                    ...subtask, 
                    is_completed: !currentState
                  }
                : subtask
            )
          };

          // Check if all subtasks are completed to update parent task state
          const allSubtasksCompleted = updatedTaskSchema.subtasks.every(
            subtask => subtask.is_completed
          );

          // Update local state first
          updatedCall.todo_items[parentTaskIndex] = {
            ...parentTask,
            task_schema: updatedTaskSchema,
            is_completed: allSubtasksCompleted,
            completed_at: allSubtasksCompleted ? new Date().toISOString() : null
          };

          const { error: updateError } = await supabase
            .from('todo_items')
            .update({
              task_schema: updatedTaskSchema,
              is_completed: allSubtasksCompleted,
              completed_at: allSubtasksCompleted ? new Date().toISOString() : null
            })
            .eq('id', parentTask.id);

          if (updateError) throw updateError;
        }
      }

      // Check if all tasks are now completed
      const allTasksCompleted = updatedCall.todo_items.every(item => item.is_completed);

      // Update local state immediately
      setCall(updatedCall);

      // If all tasks are completed, hide the section after a brief delay to allow exit animation
      if (allTasksCompleted) {
        setShowCompletedTasks(false);
      }

    } catch (err) {
      console.error('Error updating task:', err);
      // Optionally revert local state if the server update failed
      await fetchCallDetails();
    }
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;

    if (audioState.playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setAudioState(prev => ({ ...prev, playing: !prev.playing }));
  };

  const formatTime = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const getCleanPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    // Remove all non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, '');
    // If it starts with 1 and has 11 digits, remove the 1
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return cleaned.slice(1);
    }
    // If it's already 10 digits, return as is
    if (cleaned.length === 10) {
      return cleaned;
    }
    // Otherwise return the original
    return phoneNumber;
  };

  // Helper function to determine if we should show contact name or phone number
  const shouldShowContactName = (call) => {
    return call?.contact?.name && !call.contact.name.toLowerCase().includes('customer');
  };

  const getDisplayName = (call) => {
    if (shouldShowContactName(call)) {
      return call.contact.name;
    }
    return formatPhoneNumber(call.from_number);
  };

  if (loading) {
    return (
      <div
        className={hideBackButton ? "h-full flex flex-col" : "h-screen bg-gray-50 flex flex-col md:h-screen"}
      >
        {!hideBackButton && (
          <div className="hidden md:block border-b border-gray-100">
            <div className="container mx-auto py-5 sm:py-6 px-4 sm:px-6 max-w-7xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <motion.button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors"
                    whileHover={{ x: -3 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </motion.button>
                  <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Call Details</h1>
                </div>
                <motion.button
                  onClick={() => helpPanel.open()}
                  whileHover={{ 
                    scale: 1.02,
                    transition: { duration: 0.2, ease: "easeOut" }
                  }}
                  whileTap={{ 
                    scale: 0.98,
                    transition: { duration: 0.1, ease: "easeIn" }
                  }}
                  className="p-2.5 rounded-full bg-white border border-gray-200 text-gray-700 shadow-sm hover:shadow transition-all duration-200"
                  aria-label="Help"
                >
                  <Question className="h-4 w-4" />
                </motion.button>
              </div>
            </div>
          </div>
        )}
        <div className={hideBackButton ? "h-full flex flex-col" : "flex-1 flex flex-col px-0 md:px-4 lg:px-6"}>
          <div className="flex-1 flex flex-col min-h-0">
            <Card className="overflow-hidden shadow-none md:shadow-xl border-0 md:border md:border-gray-100 rounded-none md:rounded-2xl bg-white h-full flex flex-col">
              <CardContent className="p-0 h-full flex flex-col">
                {/* Skeleton Header */}
                <div className="p-5 sm:p-6 border-b border-gray-200 bg-gray-50/50 flex-shrink-0">
                  <div className="flex items-start justify-between mb-4">
                    {!hideBackButton && (
                      <div className="md:hidden">
                        <div className="w-6 h-6 bg-gray-200 rounded animate-pulse"></div>
                      </div>
                    )}
                    <div className="text-right">
                      <div className="h-3 w-20 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-5 w-32 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                </div>

                {/* Skeleton Summary */}
                <div className="p-5 sm:p-6 border-b border-gray-100 flex-shrink-0">
                  <div className="space-y-2">
                    <div className="h-4 w-full bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-4 w-4/5 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-4 w-3/5 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                </div>

                {/* Skeleton Recording Controls */}
                <div className="p-5 sm:p-6 border-b border-gray-100 flex-shrink-0">
                  <div className="flex items-center gap-4">
                    <div className="w-6 h-6 bg-gray-200 rounded-lg animate-pulse"></div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="h-3 w-8 bg-gray-200 rounded animate-pulse"></div>
                        <div className="flex-1 h-1.5 bg-gray-200 rounded-full animate-pulse"></div>
                        <div className="h-3 w-8 bg-gray-200 rounded animate-pulse"></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Skeleton Transcript */}
                <div className="flex-1 min-h-0 flex flex-col">
                  <div className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-6 py-4">
                    <div className="space-y-4">
                      {Array.from({ length: 6 }).map((_, index) => (
                        <div key={index} className="flex gap-3">
                          <div className="w-7 h-7 bg-gray-200 rounded-full animate-pulse flex-shrink-0"></div>
                          <div className="flex-1 min-w-0">
                            <div className="h-3 w-16 bg-gray-200 rounded animate-pulse mb-1"></div>
                            <div className="space-y-1">
                              <div className="h-3 w-full bg-gray-200 rounded animate-pulse"></div>
                              <div className="h-3 w-3/4 bg-gray-200 rounded animate-pulse"></div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Skeleton Audio Player - Fixed at bottom */}
                <div className="border-t border-gray-200 p-4 bg-gray-50/50">
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 bg-gray-200 rounded-lg animate-pulse"></div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="h-3 w-8 bg-gray-200 rounded animate-pulse"></div>
                        <div className="flex-1 h-1.5 bg-gray-200 rounded-full animate-pulse"></div>
                        <div className="h-3 w-8 bg-gray-200 rounded animate-pulse"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="text-center max-w-md px-6"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-50 mb-6">
            <WarningCircle className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-xl font-medium text-slate-900 mb-2">Unable to Load Call</h3>
          <p className="text-slate-600 mb-6">{error}</p>
          <motion.button
            onClick={() => navigate(-1)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-black rounded-md hover:bg-slate-800 transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Return to Calls
          </motion.button>
        </motion.div>
      </div>
    );
  }

  return (
    <div
      className={hideBackButton ? "h-full flex flex-col" : "h-screen bg-gray-50 flex flex-col md:h-screen"}
    >
      {!hideBackButton && (
        <div className="hidden md:block border-b border-gray-100">
          <div className="container mx-auto py-5 sm:py-6 px-4 sm:px-6 max-w-7xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3 sm:space-x-4">
                <motion.button
                  onClick={() => navigate(-1)}
                  className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors"
                  whileHover={{ x: -3 }}
                  transition={{ duration: 0.3 }}
                >
                  <ArrowLeft className="w-5 h-5" />
                </motion.button>
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Call Details</h1>
              </div>
              <motion.button
                onClick={() => helpPanel.open()}
                whileHover={{ 
                  scale: 1.02,
                  transition: { duration: 0.2, ease: "easeOut" }
                }}
                whileTap={{ 
                  scale: 0.98,
                  transition: { duration: 0.1, ease: "easeIn" }
                }}
                className="p-2.5 rounded-full bg-white border border-gray-200 text-gray-700 shadow-sm hover:shadow transition-all duration-200"
                aria-label="Help"
              >
                <Question className="h-4 w-4" />
              </motion.button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile-only Header - Fixed at top of viewport */}
      {call && (
        <motion.div 
          ref={mobileHeaderRef}
          variants={sectionVariants}
          className="md:hidden fixed top-0 left-0 right-0 z-50 border-b border-gray-200 bg-white/95 backdrop-blur-sm"
          style={{
            paddingTop: 0
          }}
        >
          <div className="p-4 sm:p-5">
            {/* First Row: Back Arrow + Name/Title + Copy Button + Date */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {!hideBackButton && (
                  <motion.button
                    onClick={() => navigate(-1)}
                    className="flex items-center justify-center w-6 h-6 text-gray-600 hover:text-gray-900 transition-colors flex-shrink-0"
                    whileHover={{ x: -2 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </motion.button>
                )}
                <span className="text-lg font-medium text-gray-900 truncate">
                  {getDisplayName(call)}
                </span>
                {!shouldShowContactName(call) && (
                  <motion.button
                    onClick={() => copyToClipboard(getCleanPhoneNumber(call?.from_number))}
                    className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    title="Copy phone number"
                  >
                    <Copy className="w-4 h-4" />
                  </motion.button>
                )}
              </div>
              <div className="text-right text-xs text-gray-500 flex-shrink-0">
                <div className="font-medium">{formatDate(call?.created_at)}</div>
              </div>
            </div>

            {/* Second Row: Phone Icon + Phone Number (only if showing contact name) */}
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${statusConfig[call?.call_category]?.background}`}>
                <Phone className={`w-4 h-4 ${statusConfig[call?.call_category]?.color}`} />
              </div>
              {shouldShowContactName(call) && (
                <>
                  <span className="text-sm text-gray-500 font-normal">
                    {formatPhoneNumber(call?.from_number)}
                  </span>
                  <motion.button
                    onClick={() => copyToClipboard(getCleanPhoneNumber(call?.from_number))}
                    className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    title="Copy phone number"
                  >
                    <Copy className="w-4 h-4" />
                  </motion.button>
                </>
              )}
            </div>
          </div>
        </motion.div>
      )}

      <div className={hideBackButton ? "h-full flex flex-col" : "flex-1 flex flex-col px-0 md:px-4 lg:px-6"}>
        {/* Single Card - Steve Jobs Style with Consistent Branding */}
        <div className={`flex-1 flex flex-col min-h-0 ${call ? 'md:mt-0' : ''}`}>
          <Card className={`overflow-hidden shadow-none md:shadow-xl border-0 md:border md:border-gray-100 rounded-none md:rounded-2xl bg-white h-full flex flex-col`}>
            <CardContent className="p-0 h-full flex flex-col">
              {/* Call Header - Desktop only, hidden on mobile */}
              <div className="hidden md:block p-5 sm:p-6 border-b border-gray-200 bg-gray-50/50 flex-shrink-0">
                <div className="flex items-start justify-between mb-4">
                  {!hideBackButton && (
                    <motion.button
                      onClick={() => navigate(-1)}
                      className="md:hidden flex items-center justify-center w-6 h-6 text-gray-600 hover:text-gray-900 transition-colors"
                      whileHover={{ x: -2 }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </motion.button>
                  )}
                  <div className={`text-right text-xs text-gray-500 ${!hideBackButton ? 'md:ml-0' : ''}`}>
                    <div className="font-medium">{formatDate(call?.created_at)}</div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${statusConfig[call?.call_category]?.background}`}>
                      <Phone className={`w-4 h-4 ${statusConfig[call?.call_category]?.color}`} />
                    </div>
                    <span className="text-lg font-medium text-gray-900">
                      {getDisplayName(call)}
                    </span>
                  </div>
                  {shouldShowContactName(call) && (
                    <div className="flex items-center gap-2 ml-10 sm:ml-0">
                      <span className="text-sm text-gray-500 font-normal">
                        {formatPhoneNumber(call?.from_number)}
                      </span>
                      <motion.button
                        onClick={() => copyToClipboard(getCleanPhoneNumber(call?.from_number))}
                        className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        title="Copy phone number"
                      >
                        <Copy className="w-4 h-4" />
                      </motion.button>
                    </div>
                  )}
                  {!shouldShowContactName(call) && (
                    <motion.button
                      onClick={() => copyToClipboard(getCleanPhoneNumber(call?.from_number))}
                      className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors ml-10 sm:ml-0"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      title="Copy phone number"
                    >
                      <Copy className="w-4 h-4" />
                    </motion.button>
                  )}
                </div>
              </div>

              {/* Scrollable Content Area - Add top and bottom padding on mobile when header/audio player exists */}
              <div className={`flex-1 min-h-0 overflow-y-auto relative ${call?.recording_url ? 'md:pb-0 pb-20' : ''}`}>
                {/* Task Section - sticky at top of scroll container */}
                <TaskSection 
                  callId={call?.id}
                  variant="call"
                  onTaskUpdate={(updatedTasks) => {
                    // Update the call's todo_items in local state
                    if (call) {
                      setCall(prev => ({
                        ...prev,
                        todo_items: updatedTasks
                      }));
                    }
                  }}
                />

                {/* Scrollable content */}
                <div className="p-5 sm:p-6 space-y-6">
                  {/* Summary */}
                  <div className="border-b border-gray-100 pb-6">
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {call?.summary}
                    </p>
                  </div>

                  {/* Transcript */}
                  {call?.transcript && (
                      <div className="space-y-4">
                        {call.transcript
                          .split('\n')
                          .filter(line => line.trim())
                          .map((line, index) => {
                            const aiName = 'Bella';
                            const isAI = line.trim().startsWith('assistant:');
                            const isUser = line.trim().startsWith('user:');

                            if (isAI || isUser) {
                              const message = isAI 
                                ? line.replace('assistant:', '').trim() 
                                : line.replace('user:', '').trim();

                              const displayName = isAI 
                                ? aiName 
                                : (call.contact?.name?.toLowerCase().includes('customer')
                                    ? formatPhoneNumber(call.from_number)
                                    : call.contact?.name || 'Customer');

                              return (
                                <motion.div 
                                  key={index} 
                                  className="flex gap-3"
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ 
                                    delay: index * 0.03,
                                    duration: 0.3
                                  }}
                                >
                                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                                    isAI ? 'bg-black text-white' : 'bg-gray-100 text-gray-600'
                                  }`}>
                                    <span className="text-xs font-medium">
                                      {isAI ? 'B' : displayName.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs text-gray-500 mb-1 font-medium">
                                      {displayName}
                                      {isAI && <span className="ml-1 text-gray-400 font-normal">AI Assistant</span>}
                                    </div>
                                    <div className="text-sm text-gray-900 leading-relaxed">
                                      {message}
                                    </div>
                                  </div>
                                </motion.div>
                              );
                            }
                            return null;
                          })}
                    </div>
                  )}
                </div>
              </div>

              {/* Audio Player - Desktop: Fixed at bottom of card, Mobile: Will be moved outside */}
              {call?.recording_url && (
                <motion.div 
                  variants={sectionVariants}
                  className="hidden md:block border-t border-gray-200 p-4 bg-gray-50/50"
                >
                  <div className="flex items-center gap-4">
                    {/* Play Button */}
                    <motion.button
                      onClick={handlePlayPause}
                      className="flex-shrink-0 w-8 h-8 rounded-lg bg-black hover:bg-gray-800 flex items-center justify-center shadow-lg transition-all duration-200"
                      whileHover={{ 
                        scale: 1.05,
                        boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                        transition: { duration: 0.2 }
                      }}
                      whileTap={{ 
                        scale: 0.95,
                        transition: { duration: 0.1 }
                      }}
                    >
                      <AnimatePresence mode="wait">
                        {audioState.playing ? (
                          <motion.div
                            key="pause"
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            transition={{ duration: 0.15 }}
                          >
                            <Pause className="w-4 h-4 text-white" />
                          </motion.div>
                        ) : (
                          <motion.div
                            key="play"
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            transition={{ duration: 0.15 }}
                          >
                            <Play className="w-4 h-4 text-white ml-0.5" />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.button>

                    {/* Progress Bar and Time */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <div className="text-xs font-medium text-gray-900 tabular-nums">
                          {formatTime(audioState.currentTime)}
                        </div>
                        <div className="flex-1 relative h-2">
                          <motion.div 
                            className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1.5 bg-gray-200 rounded-full overflow-hidden"
                            whileHover={{ height: '6px' }}
                            transition={{ duration: 0.2 }}
                          >
                            <motion.div 
                              className="absolute left-0 top-0 h-full bg-black rounded-full"
                              style={{ 
                                width: `${(audioState.currentTime / audioState.duration) * 100}%` 
                              }}
                              transition={{ duration: 0.1 }}
                            />
                          </motion.div>
                          <input
                            type="range"
                            min="0"
                            max={audioState.duration}
                            value={audioState.currentTime}
                            onChange={(e) => {
                              const time = parseFloat(e.target.value);
                              if (audioRef.current) {
                                audioRef.current.currentTime = time;
                              }
                              setAudioState(prev => ({ ...prev, currentTime: time }));
                            }}
                            className="absolute left-0 top-1/2 -translate-y-1/2 w-full opacity-0 cursor-pointer h-6"
                          />
                        </div>
                        <div className="text-xs text-gray-500 tabular-nums">
                          {formatTime(audioState.duration)}
                        </div>
                      </div>
                    </div>
                  </div>

                  <audio
                    ref={audioRef}
                    src={call.recording_url}
                    onTimeUpdate={() => {
                      if (audioRef.current) {
                        setAudioState(prev => ({
                          ...prev,
                          currentTime: audioRef.current.currentTime
                        }));
                      }
                    }}
                    onLoadedMetadata={(e) => {
                      if (!call.corrected_duration) {
                        setAudioState(prev => ({
                          ...prev,
                          duration: e.target.duration
                        }));
                      } else {
                        const correctedDuration = Math.round(parseFloat(call.corrected_duration));
                        setAudioState(prev => ({
                          ...prev,
                          duration: correctedDuration
                        }));
                      }
                    }}
                    onEnded={() => setAudioState(prev => ({ ...prev, playing: false }))}
                    className="hidden"
                  />
                </motion.div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Mobile-only Audio Player - Fixed at bottom of viewport */}
      {call?.recording_url && (
        <motion.div 
          variants={sectionVariants}
          className="md:hidden fixed bottom-0 left-0 right-0 z-50 border-t border-gray-200 bg-white/95 backdrop-blur-sm p-4 safe-area-pb"
          style={{
            paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))'
          }}
        >
          <div className="flex items-center gap-4">
            {/* Play Button */}
            <motion.button
              onClick={handlePlayPause}
              className="flex-shrink-0 w-10 h-10 rounded-xl bg-black hover:bg-gray-800 flex items-center justify-center shadow-lg transition-all duration-200"
              whileHover={{ 
                scale: 1.05,
                boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                transition: { duration: 0.2 }
              }}
              whileTap={{ 
                scale: 0.95,
                transition: { duration: 0.1 }
              }}
            >
              <AnimatePresence mode="wait">
                {audioState.playing ? (
                  <motion.div
                    key="pause"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    <Pause className="w-5 h-5 text-white" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="play"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    <Play className="w-5 h-5 text-white ml-0.5" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>

            {/* Progress Bar and Time */}
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <div className="text-xs font-medium text-gray-900 tabular-nums">
                  {formatTime(audioState.currentTime)}
                </div>
                <div className="flex-1 relative h-2">
                  <motion.div 
                    className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-2 bg-gray-200 rounded-full overflow-hidden"
                    whileHover={{ height: '8px' }}
                    transition={{ duration: 0.2 }}
                  >
                    <motion.div 
                      className="absolute left-0 top-0 h-full bg-black rounded-full"
                      style={{ 
                        width: `${(audioState.currentTime / audioState.duration) * 100}%` 
                      }}
                      transition={{ duration: 0.1 }}
                    />
                  </motion.div>
                  <input
                    type="range"
                    min="0"
                    max={audioState.duration}
                    value={audioState.currentTime}
                    onChange={(e) => {
                      const time = parseFloat(e.target.value);
                      if (audioRef.current) {
                        audioRef.current.currentTime = time;
                      }
                      setAudioState(prev => ({ ...prev, currentTime: time }));
                    }}
                    className="absolute left-0 top-1/2 -translate-y-1/2 w-full opacity-0 cursor-pointer h-8"
                  />
                </div>
                <div className="text-xs text-gray-500 tabular-nums">
                  {formatTime(audioState.duration)}
                </div>
              </div>
            </div>
          </div>

          {/* Audio element - shared between desktop and mobile */}
          <audio
            ref={audioRef}
            src={call.recording_url}
            onTimeUpdate={() => {
              if (audioRef.current) {
                setAudioState(prev => ({
                  ...prev,
                  currentTime: audioRef.current.currentTime
                }));
              }
            }}
            onLoadedMetadata={(e) => {
              if (!call.corrected_duration) {
                setAudioState(prev => ({
                  ...prev,
                  duration: e.target.duration
                }));
              } else {
                const correctedDuration = Math.round(parseFloat(call.corrected_duration));
                setAudioState(prev => ({
                  ...prev,
                  duration: correctedDuration
                }));
              }
            }}
            onEnded={() => setAudioState(prev => ({ ...prev, playing: false }))}
            className="hidden"
          />
        </motion.div>
      )}

      {/* Help Panel */}
      <HelpPanel
        isOpen={helpPanel.isOpen}
        onClose={helpPanel.close}
        title={helpPanel.config.title}
        content={helpPanel.config.content}
        sections={helpPanel.config.sections}
        position={helpPanel.config.position}
        size={helpPanel.config.size}
      />
    </div>
  );
};

export default CallDetailsPage;